import { useState } from 'react'
import { CheckCircle, Download, Copy, ExternalLink, Shield, MessageCircle } from 'lucide-react'
import type { ElectronicSignatureRecord } from '../types'
import { buildWhatsappLink, buildSignatureShareMessage } from '@/shared/lib/whatsapp'

interface Props {
  record: ElectronicSignatureRecord
  signedPdfBytes: Uint8Array
  isDigital: boolean
  onSignAnother: () => void
}

export function ElectronicSignatureResult({ record, signedPdfBytes, isDigital, onSignAnother }: Props) {
  const [copied, setCopied] = useState(false)

  const fullUrl = `${window.location.origin}/${isDigital ? 's' : 'se'}/${record.signature_id}`
  const shortUrl = record.short_id ? `${window.location.origin}/v/${record.short_id}` : null
  const verificationUrl = shortUrl || fullUrl

  const handleDownload = () => {
    const blob = new Blob([signedPdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = record.signed_filename || 'documento_firmado.pdf'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(verificationUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="bg-white dark:bg-legal-800 rounded-xl shadow-md border border-green-200 dark:border-green-800 p-6">
      <div className="flex items-start gap-3 mb-4">
        {isDigital ? (
          <Shield className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={28} />
        ) : (
          <CheckCircle className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={28} />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-legal-900 dark:text-white">
              {isDigital ? 'Firma digital aplicada' : 'Firma electrónica aplicada'}
            </h2>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isDigital
                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
            }`}>
              {isDigital ? 'art. 9 PKI' : 'art. 5 simple'}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{record.signed_filename}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 text-sm">
        <div className="bg-legal-50 dark:bg-legal-900/40 p-3 rounded-lg">
          <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Firmante</p>
          <p className="text-legal-900 dark:text-white font-medium">{record.signer_name || 'Firmante'}</p>
        </div>
        <div className="bg-legal-50 dark:bg-legal-900/40 p-3 rounded-lg">
          <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Fecha</p>
          <p className="text-legal-900 dark:text-white font-medium">
            {new Date(record.signed_at).toLocaleString('es-AR')}
          </p>
        </div>
        <div className="bg-legal-50 dark:bg-legal-900/40 p-3 rounded-lg sm:col-span-2">
          <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Hash SHA-256 del PDF firmado</p>
          <p className="font-mono text-xs text-legal-700 dark:text-legal-300 break-all">{record.signed_hash}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <button
          onClick={handleDownload}
          className="flex-1 flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
        >
          <Download size={18} />
          Descargar PDF firmado
        </button>
        <a
          href={buildWhatsappLink(buildSignatureShareMessage(verificationUrl))}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
        >
          <MessageCircle size={18} />
          Compartir por WhatsApp
        </a>
      </div>
      <div className="mb-4">
        <button
          onClick={handleCopyUrl}
          className="w-full flex items-center justify-center gap-2 bg-legal-100 dark:bg-legal-700 hover:bg-legal-200 dark:hover:bg-legal-600 text-legal-800 dark:text-white py-2.5 px-4 rounded-lg text-sm font-medium transition-colors"
        >
          <Copy size={16} />
          {copied ? 'URL copiada' : 'Copiar URL de verificación'}
        </button>
      </div>

      <div className="text-xs text-gray-600 dark:text-gray-400 mb-4 space-y-1.5">
        {shortUrl && (
          <div>
            <span className="font-medium text-legal-700 dark:text-legal-300">URL corta (en QR):</span>{' '}
            <a
              href={shortUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 break-all"
            >
              {shortUrl}
              <ExternalLink size={12} />
            </a>
          </div>
        )}
        <div>
          <span className="font-medium text-legal-700 dark:text-legal-300">URL completa:</span>{' '}
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 break-all"
          >
            {fullUrl}
            <ExternalLink size={12} />
          </a>
        </div>
      </div>

      <div className={`rounded-lg p-3 text-sm border ${
        isDigital
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200'
          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-900 dark:text-yellow-200'
      }`}>
        <p className="font-medium mb-1">
          Marco legal: {isDigital ? 'art. 9 Ley 25.506 (firma digital con PKI)' : 'art. 5 Ley 25.506 (firma electrónica simple)'}
        </p>
        <p className="text-xs">{record.legal_implication}</p>
      </div>

      <button
        onClick={onSignAnother}
        className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
      >
        Firmar otro documento
      </button>
    </div>
  )
}
